import { getDomain } from "./getTld"
import { getRootMetadata } from "./getRootMetadata"
import { isDev } from "./environment"

/** Короткое имя бренда для PWA-манифеста — тот же список брендов, что в generate-sitemap.js. */
const SHORT_NAMES: Record<string, Record<string, string>> = {
  cz: { v0: "Finero", v1: "Goodfin", v2: "Pujckafon", v3: "Goodfin", v4: "Incash" },
  cz_test: { v0: "Finero", v1: "Goodfin", v2: "Pujckafon", v3: "Goodfin", v4: "Incash" },
  kz: { v0: "Finero" },
  kz_kz: { v0: "Finero" },
  ro: { v0: "Incash", v4: "Incash" },
  mx: { v0: "Finero" },
  mx_2: { v0: "Finero" },
  za: { v0: "Twalalend", v5: "Twalalend" },
  za_2: { v0: "Yebofin" }
}

/** Пары {region}-{variant}, для которых в public/ лежат logo192-*.png и logo512-*.png. */
const LOGO_KEYS = new Set([
  "cz-v0",
  "cz-v1",
  "cz-v2",
  "cz-v3",
  "kz-v0",
  "kz_kz-v0",
  "mx-v0",
  "mx_2-v0",
  "ro-v0",
  "za-v5"
])

/** Пары {region}-{variant}, для которых в public/ лежит favicon-*.ico. */
const FAVICON_KEYS = new Set([...LOGO_KEYS, "cz-v4", "ro-v4"])

/** У Incash нет своих logo192/logo512 — как и generate-sitemap.js, берём квадратную иконку бренда. */
const INCASH_ICON = "/incash/incash_favicon_png.png"

export interface IBrandIconPaths {
  favicon: string | null
  logo192: string | null
  logo512: string | null
}

/**
 * Иконки бренда из public/ (favicon|logo192|logo512-{region}-{variant}, как в generate-sitemap.js).
 * null — для пары нет файла: лучше не указывать иконку, чем отдать 404 в манифест или og:image.
 */
export function getBrandIconPaths(region: string, variant: string): IBrandIconPaths {
  const key = `${region}-${variant}`
  const favicon = FAVICON_KEYS.has(key) ? `/favicon-${key}.ico` : null

  if (LOGO_KEYS.has(key)) {
    return { favicon, logo192: `/logo192-${key}.png`, logo512: `/logo512-${key}.png` }
  }
  if (SHORT_NAMES[region]?.[variant] === "Incash") {
    return { favicon, logo192: INCASH_ICON, logo512: INCASH_ICON }
  }
  return { favicon, logo192: null, logo512: null }
}

/**
 * Обновляет favicon и PWA-манифест под активные регион/вариант.
 * Манифест собирается на лету (Blob URL) — статичных manifest-{region}-{variant}.json
 * файлов на сборку под каждую комбинацию нет. В dev-режиме манифест не генерируется вовсе.
 */
export const setSiteMeta = (variant: string) => {
  const region = getDomain()
  const { favicon: faviconHref, logo192, logo512 } = getBrandIconPaths(region, variant)

  if (faviconHref) {
    document.querySelectorAll<HTMLLinkElement>('link[rel~="icon"]').forEach(el => {
      el.href = faviconHref
    })
  }

  if (isDev) return

  const manifestLink = document.getElementById("manifest") as HTMLLinkElement | null
  if (!manifestLink) return

  const { title, description } = getRootMetadata()
  const shortName = SHORT_NAMES[region]?.[variant] ?? title

  const manifest = {
    short_name: shortName,
    name: title,
    description,
    icons: [
      logo192 && { src: logo192, type: "image/png", sizes: "192x192" },
      logo512 && { src: logo512, type: "image/png", sizes: "512x512" },
      faviconHref && { src: faviconHref, type: "image/x-icon", sizes: "48x48", purpose: "any" }
    ].filter(Boolean),
    start_url: ".",
    display: "standalone",
    theme_color: "#000000",
    background_color: "#ffffff"
  }

  const blobUrl = URL.createObjectURL(
    new Blob([JSON.stringify(manifest)], { type: "application/manifest+json" })
  )
  manifestLink.href = blobUrl
}
